/* CTR Menu Nav 2.0 — desktop mega menu and mobile slide-out drawer. */

class CtrMenuNav extends HTMLElement {
  connectedCallback() {
    this.bar = this.querySelector('[data-ctr-nav-bar]');
    this.scrim = this.querySelector('[data-ctr-nav-scrim]');
    this.drawer = this.querySelector('[data-ctr-nav-drawer]');
    this.triggers = Array.from(this.querySelectorAll('[data-ctr-nav-trigger]'));
    this.panels = Array.from(this.querySelectorAll('[data-ctr-nav-panel]'));
    this.subpanels = Array.from(this.querySelectorAll('[data-ctr-nav-subpanel]'));

    this.openPanel = null;
    this.lastFocused = null;
    this.scrollY = 0;
    this.desktopQuery = window.matchMedia('(min-width: 990px)');

    this.onKeydown = this.handleKeydown.bind(this);
    this.onDocumentClick = this.handleDocumentClick.bind(this);
    this.onResize = this.handleResize.bind(this);

    this.bindTriggers();
    this.bindDrawer();
    this.bindSubpanels();
    this.bindCarousels();

    document.addEventListener('keydown', this.onKeydown);
    document.addEventListener('click', this.onDocumentClick);
    window.addEventListener('resize', this.onResize);

    this.setHeaderHeight();
  }

  disconnectedCallback() {
    document.removeEventListener('keydown', this.onKeydown);
    document.removeEventListener('click', this.onDocumentClick);
    window.removeEventListener('resize', this.onResize);
    this.unlockScroll();
  }

  /* ---------------------------------------------------------------- setup */

  bindTriggers() {
    this.triggers.forEach((trigger) => {
      const panel = this.panelFor(trigger);
      if (!panel) return;

      trigger.addEventListener('click', (event) => {
        event.preventDefault();
        if (this.openPanel === panel) {
          this.closePanels();
        } else {
          this.showPanel(panel, trigger);
        }
      });
    });
  }

  bindDrawer() {
    const open = this.querySelector('[data-ctr-nav-drawer-open]');
    const close = this.querySelector('[data-ctr-nav-drawer-close]');

    if (open) open.addEventListener('click', () => this.openDrawer());
    if (close) close.addEventListener('click', () => this.closeDrawer());
    if (this.scrim) this.scrim.addEventListener('click', () => this.closeAll());

    this.drawerScrolls = Array.from(this.querySelectorAll('[data-ctr-nav-drawer-scroll]'));
    this.drawerScrolls.forEach((el) => {
      el.addEventListener('scroll', () => this.updateDrawerFade(el), { passive: true });
    });
  }

  /* Fade only while there is still something below to scroll to. */
  updateDrawerFade(el) {
    const more = el.scrollHeight - el.scrollTop - el.clientHeight > 1;
    el.classList.toggle('has-more', more);
  }

  updateDrawerFades() {
    this.drawerScrolls.forEach((el) => this.updateDrawerFade(el));
  }

  bindSubpanels() {
    this.querySelectorAll('[data-ctr-nav-subpanel-open]').forEach((button) => {
      const panel = this.querySelector(`#${CSS.escape(button.getAttribute('aria-controls'))}`);
      if (!panel) return;

      button.addEventListener('click', () => {
        panel.classList.add('is-open');
        button.setAttribute('aria-expanded', 'true');
        this.focusFirst(panel);
        this.updateDrawerFades();
      });
    });

    this.querySelectorAll('[data-ctr-nav-subpanel-close]').forEach((button) => {
      const panel = button.closest('[data-ctr-nav-subpanel]');
      if (!panel) return;

      button.addEventListener('click', () => {
        panel.classList.remove('is-open');
        const opener = this.querySelector(`[aria-controls="${panel.id}"]`);
        if (opener) {
          opener.setAttribute('aria-expanded', 'false');
          opener.focus({ preventScroll: true });
        }
      });
    });
  }

  panelFor(trigger) {
    const id = trigger.getAttribute('aria-controls');
    return id ? this.querySelector(`#${CSS.escape(id)}`) : null;
  }

  /* -------------------------------------------------------------- carousel */

  bindCarousels() {
    this.carousels = [];

    this.querySelectorAll('[data-ctr-nav-carousel]').forEach((carousel) => {
      const panel = carousel.closest('[data-ctr-nav-panel]');
      const track = panel ? panel.querySelector('[data-ctr-nav-track]') : null;
      if (!track) return;

      const instance = {
        track,
        carousel,
        dotsEl: carousel.querySelector('[data-ctr-nav-dots]'),
        prevBtn: carousel.querySelector('[data-ctr-nav-prev]'),
        nextBtn: carousel.querySelector('[data-ctr-nav-next]'),
      };

      instance.prevBtn.addEventListener('click', () => this.page(instance, -1));
      instance.nextBtn.addEventListener('click', () => this.page(instance, 1));

      // Scroll fires continuously; settle before recalculating the active dot.
      let settle;
      track.addEventListener('scroll', () => {
        clearTimeout(settle);
        settle = setTimeout(() => this.updateCarousel(instance), 80);
      });

      this.carousels.push(instance);
    });

    this.buildCarousels();
  }

  buildCarousels() {
    this.carousels.forEach((instance) => this.buildCarousel(instance));
  }

  /* Stepping is per card, not per viewport: with 5 cards and 4 visible there is
     only one card's worth of overflow, which a viewport-sized page would treat
     as no page at all. */
  metrics(instance) {
    const card = instance.track.querySelector('.ctr-nav-card');
    if (!card || !instance.track.clientWidth) return null;

    const gap = parseFloat(getComputedStyle(instance.track).columnGap) || 0;
    const step = card.getBoundingClientRect().width + gap;
    const maxScroll = instance.track.scrollWidth - instance.track.clientWidth;

    return { step, maxScroll, positions: Math.max(1, Math.round(maxScroll / step) + 1) };
  }

  currentIndex(instance, m) {
    return Math.min(m.positions - 1, Math.max(0, Math.round(instance.track.scrollLeft / m.step)));
  }

  buildCarousel(instance) {
    // Below the desktop breakpoint (or in a closed panel) the track measures 0.
    if (!instance.track.clientWidth) return;

    const m = this.metrics(instance);
    const overflows = m && m.maxScroll > 1;
    instance.carousel.hidden = !overflows;
    if (!overflows) return;

    if (instance.dotsEl.childElementCount !== m.positions) {
      instance.dotsEl.textContent = '';
      for (let i = 0; i < m.positions; i += 1) {
        const dot = document.createElement('button');
        dot.type = 'button';
        dot.className = 'ctr-nav__dot';
        dot.setAttribute('role', 'tab');
        dot.setAttribute('aria-label', `${i + 1}`);
        dot.addEventListener('click', () => {
          instance.track.scrollTo({ left: i * m.step, behavior: 'smooth' });
        });
        instance.dotsEl.appendChild(dot);
      }
    }

    this.updateCarousel(instance);
  }

  updateCarousel(instance) {
    if (instance.carousel.hidden) return;
    const m = this.metrics(instance);
    if (!m) return;

    const current = this.currentIndex(instance, m);
    Array.from(instance.dotsEl.children).forEach((dot, i) => {
      dot.setAttribute('aria-selected', i === current ? 'true' : 'false');
    });

    instance.prevBtn.disabled = instance.track.scrollLeft <= 1;
    instance.nextBtn.disabled = instance.track.scrollLeft >= m.maxScroll - 1;
  }

  page(instance, direction) {
    const m = this.metrics(instance);
    if (!m) return;
    instance.track.scrollBy({ left: direction * m.step, behavior: 'smooth' });
  }

  /* -------------------------------------------------------- desktop panels */

  showPanel(panel, trigger) {
    this.closePanels({ keepScrim: true });

    panel.classList.add('is-open');
    panel.removeAttribute('aria-hidden');
    trigger.setAttribute('aria-expanded', 'true');

    this.openPanel = panel;
    this.lastFocused = trigger;
    this.classList.add('is-open');
    this.lockScroll();

    // Measure now the panel is laid out.
    this.buildCarousels();
  }

  closePanels(options = {}) {
    this.panels.forEach((panel) => {
      panel.classList.remove('is-open');
      panel.setAttribute('aria-hidden', 'true');
    });
    this.triggers.forEach((trigger) => trigger.setAttribute('aria-expanded', 'false'));

    this.openPanel = null;

    if (!options.keepScrim) {
      this.classList.remove('is-open');
      if (!this.isDrawerOpen()) this.unlockScroll();
    }
  }

  /* --------------------------------------------------------- mobile drawer */

  isDrawerOpen() {
    return this.classList.contains('is-drawer-open');
  }

  openDrawer() {
    // Finish any pending close so a stale sub-panel can't flash.
    if (this.pendingSubpanelReset) this.pendingSubpanelReset();

    this.lastFocused = document.activeElement;
    this.classList.add('is-drawer-open', 'is-open');
    if (this.drawer) {
      this.drawer.removeAttribute('aria-hidden');
      this.focusFirst(this.drawer);
    }
    this.lockScroll();

    // Measure now the drawer is laid out.
    this.updateDrawerFades();
  }

  closeDrawer() {
    this.classList.remove('is-drawer-open');
    if (this.drawer) this.drawer.setAttribute('aria-hidden', 'true');
    this.resetSubpanelsAfterSlide();

    if (!this.openPanel) {
      this.classList.remove('is-open');
      this.unlockScroll();
    }

    this.restoreFocus();
  }

  /* Hold an open sub-panel in place while the drawer slides out, then reset
     it unseen — resetting it immediately makes the two move apart. */
  resetSubpanelsAfterSlide() {
    const open = this.subpanels.filter((panel) => panel.classList.contains('is-open'));
    if (!open.length) return;

    const reset = () => {
      clearTimeout(this.subpanelResetTimer);
      if (this.drawer) this.drawer.removeEventListener('transitionend', onEnd);
      this.pendingSubpanelReset = null;

      open.forEach((panel) => {
        panel.style.transition = 'none';
        panel.classList.remove('is-open');
        // Flush so the next open transitions again.
        void panel.offsetHeight;
        panel.style.transition = '';

        const opener = this.querySelector(`[aria-controls="${panel.id}"]`);
        if (opener) opener.setAttribute('aria-expanded', 'false');
      });
    };
    const onEnd = (event) => {
      if (event.target === this.drawer && event.propertyName === 'transform') reset();
    };

    this.pendingSubpanelReset = reset;
    if (this.drawer) this.drawer.addEventListener('transitionend', onEnd);
    // Fallback: a display:none drawer (breakpoint crossing) never transitions.
    this.subpanelResetTimer = setTimeout(reset, 400);
  }

  closeAll() {
    const hadDrawer = this.isDrawerOpen();
    this.closePanels();
    if (hadDrawer) this.closeDrawer();
    else this.restoreFocus();
  }

  /* ---------------------------------------------------------------- events */

  handleKeydown(event) {
    if (event.key === 'Escape' && (this.openPanel || this.isDrawerOpen())) {
      // Close one level at a time.
      const openSub = this.subpanels.find((panel) => panel.classList.contains('is-open'));
      if (openSub) {
        const closer = openSub.querySelector('[data-ctr-nav-subpanel-close]');
        if (closer) closer.click();
        return;
      }
      this.closeAll();
      return;
    }

    if (event.key === 'Tab' && this.isDrawerOpen() && this.drawer) {
      this.trapFocus(event, this.drawer);
    }
  }

  handleDocumentClick(event) {
    if (!this.openPanel) return;
    if (this.contains(event.target)) return;
    this.closePanels();
    this.restoreFocus();
  }

  handleResize() {
    this.setHeaderHeight();
    this.buildCarousels();
    this.updateDrawerFades();

    // Crossing the breakpoint with something open would strand the scroll lock.
    if (this.desktopQuery.matches && this.isDrawerOpen()) this.closeDrawer();
    if (!this.desktopQuery.matches && this.openPanel) this.closePanels();
  }

  /* --------------------------------------------------------------- helpers */

  setHeaderHeight() {
    // A hidden instance (e.g. A/B tested out) must not zero the global var.
    if (!this.bar || !this.bar.offsetHeight) return;
    document.documentElement.style.setProperty('--header-height', `${this.bar.offsetHeight}px`);
  }

  lockScroll() {
    if (document.body.classList.contains('ctr-nav-scroll-locked')) return;
    this.scrollY = window.scrollY;

    // Measure before hiding it so CSS can reserve the gutter.
    const scrollbarWidth = window.innerWidth - document.documentElement.clientWidth;
    document.documentElement.style.setProperty('--ctr-nav-scrollbar-width', `${scrollbarWidth}px`);

    document.body.classList.add('ctr-nav-scroll-locked');
    // position:fixed is what holds iOS Safari still; top preserves the offset.
    document.body.style.position = 'fixed';
    document.body.style.top = `-${this.scrollY}px`;
    document.body.style.width = '100%';
  }

  unlockScroll() {
    if (!document.body.classList.contains('ctr-nav-scroll-locked')) return;
    document.body.classList.remove('ctr-nav-scroll-locked');
    document.body.style.position = '';
    document.body.style.top = '';
    document.body.style.width = '';
    document.documentElement.style.removeProperty('--ctr-nav-scrollbar-width');
    window.scrollTo(0, this.scrollY);
  }

  focusableIn(container) {
    return Array.from(
      container.querySelectorAll(
        'a[href], button:not([disabled]), input:not([disabled]), select:not([disabled]), textarea:not([disabled]), [tabindex]:not([tabindex="-1"])'
      )
    ).filter((element) => element.offsetParent !== null);
  }

  focusFirst(container) {
    const focusable = this.focusableIn(container);
    // preventScroll: focusing the header would scroll the page up to it.
    if (focusable.length) focusable[0].focus({ preventScroll: true });
  }

  trapFocus(event, container) {
    const focusable = this.focusableIn(container);
    if (!focusable.length) return;

    const first = focusable[0];
    const last = focusable[focusable.length - 1];

    if (event.shiftKey && document.activeElement === first) {
      event.preventDefault();
      last.focus();
    } else if (!event.shiftKey && document.activeElement === last) {
      event.preventDefault();
      first.focus();
    }
  }

  restoreFocus() {
    if (this.lastFocused && typeof this.lastFocused.focus === 'function') {
      // preventScroll: a plain focus() would undo unlockScroll()'s restore.
      this.lastFocused.focus({ preventScroll: true });
    }
    this.lastFocused = null;
  }
}

if (!customElements.get('ctr-menu-nav')) {
  customElements.define('ctr-menu-nav', CtrMenuNav);
}
